import type { Middleware } from "openapi-fetch";

/**
 * Sets `Accept-Language` on every request from the active UI locale.
 *
 * The backend honors `Accept-Language` for human-readable response fields
 * (`displayName`, `departmentName`, `title`, validation `message`, etc.) —
 * without this header it defaults to English even when the user has the
 * UI set to Arabic.
 *
 * Source of truth is `<html lang="…">`, which `@linkhr/i18n` keeps in sync
 * with i18next's active language at all times (see
 * `packages/i18n/src/direction.ts`). Reading from the DOM keeps this
 * middleware free of any hard dependency on i18n — `@linkhr/api-client`
 * stays a leaf package consumable by any app.
 *
 * In Node / SSR / pre-mount contexts where `document` is undefined, the
 * middleware no-ops (no header) and lets the backend fall back to its
 * default locale. We never overwrite an Accept-Language header the caller
 * already set deliberately.
 */
export const localeMiddleware: Middleware = {
  onRequest({ request }) {
    if (typeof document === "undefined") return undefined;
    if (request.headers.has("Accept-Language")) return undefined;

    const lang = document.documentElement.lang;
    if (lang.length === 0) return undefined;

    // Build a quality-weighted list so the backend can fall back to English
    // when a string isn't translated yet — e.g. `ar, en;q=0.8`.
    const value = lang.toLowerCase().startsWith("ar") ? "ar, en;q=0.8" : "en, ar;q=0.8";
    request.headers.set("Accept-Language", value);
    return request;
  },
};
